import { useState } from 'react';
import {
  Box,
  Button,
  List,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  useMediaQuery,
} from '@mui/material';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import { useConfirm } from 'material-ui-confirm';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { FileItem, FileUploader } from '@/common';
import { useCreatePageStore } from './useCreatePageStore';
import { mergeFilesAndRemoveDuplicates } from '@/core';

type FileUploadModalProps = {
  show: boolean;
  setShow: (show: boolean) => void;
};

const FileUploadModal = ({ show, setShow }: FileUploadModalProps) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const createPageStore = useCreatePageStore();

  return (
    <Dialog
      fullScreen={fullScreen}
      open={show}
      onClose={() => setShow(false)}
      aria-labelledby="responsive-dialog-title"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h5">{t('Upload files', { defaultValue: 'Upload files' })}</Typography>
        <CloseIcon sx={{ fontSize: '2rem', cursor: 'pointer' }} onClick={() => setShow(false)} />
      </DialogTitle>
      <DialogContent>
        <FileUploader
          onUploadFinished={(allFilesSuccessfullyUploaded, files) => {
            createPageStore.setFiles(
              mergeFilesAndRemoveDuplicates(
                createPageStore.files,
                files.map((f) => f.file)
              )
            );

            if (allFilesSuccessfullyUploaded) {
              setShow(false);
            }
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

const FilesCreate = () => {
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const confirm = useConfirm();
  const { t } = useTranslation();
  const createPageStore = useCreatePageStore();

  return (
    <>
      <FileUploadModal show={showFileUploadModal} setShow={setShowFileUploadModal} />

      <Box>
        {createPageStore.files.length > 0 ? (
          <List>
            {createPageStore.files.map((item) => (
              <FileItem
                key={item.name}
                name={item.name}
                fileName={item.name}
                onDelete={() => {
                  confirm({
                    description: `${t('Are you sure you want to remove', {
                      defaultValue: 'Are you sure you want to remove',
                    })} ${item.name} ?`,
                  }).then(() =>
                    createPageStore.setFiles(
                      createPageStore.files.filter((file) => file.name !== item.name)
                    )
                  );
                }}
              />
            ))}
          </List>
        ) : (
          <Typography variant="subtitle1">{t('No files', { defaultValue: 'No files' })}</Typography>
        )}
      </Box>
      <Button
        onClick={() => setShowFileUploadModal(true)}
        variant="outlined"
        startIcon={<AddIcon />}
        fullWidth
      >
        {t('Add files', { defaultValue: 'Add files' })}
      </Button>
    </>
  );
};

export default FilesCreate;
